import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Send, ShieldCheck, ExternalLink } from "lucide-react";
import { listApprovals, listCampaigns } from "@/lib/chiroApi";
import ScorePill from "@/components/ScorePill";

export default function OutreachQueue() {
    const [data, setData] = useState(null);
    const [campaigns, setCampaigns] = useState([]);

    useEffect(() => {
        Promise.all([listApprovals(), listCampaigns()]).then(([d, c]) => {
            setData(d);
            setCampaigns(c);
        });
    }, []);

    const cmpName = (id) => campaigns.find((c) => c.id === id)?.name || "—";
    const approved = useMemo(() => (data ? data.approved : []), [data]);
    const sent = useMemo(() => (data ? data.manually_sent : []), [data]);

    return (
        <div data-testid="outreach-queue-page" className="max-w-5xl">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#7cc93c] mb-2">
                // outreach queue · post-approval, manual send only
            </div>
            <h1 className="text-3xl font-bold tracking-tight mb-2">Outreach Queue</h1>
            <p className="text-[#9ca3af] text-sm mb-6 max-w-2xl">
                Approved drafts that you've copied to your clipboard and need to paste into
                Gmail. After sending outside ProofCommand, click <strong>Mark as manually sent</strong>{" "}
                back on the Approvals page. ProofCommand never sends outreach.
            </p>

            <div
                className="mb-6 p-3 border border-[#ff9f0a]/40 bg-[#ff9f0a]/5 rounded-[2px] flex items-start gap-2 text-[12px]"
                data-testid="outreach-queue-no-autosend-banner"
            >
                <ShieldCheck className="w-4 h-4 text-[#ff9f0a] shrink-0 mt-0.5" />
                <div className="text-[#d1d5db]">
                    <strong className="text-[#ff9f0a]">Auto-send: disabled.</strong> No mailbox is
                    connected. Status "manually sent" is set only by your explicit click after
                    sending outside the app.
                </div>
            </div>

            {/* Approved — awaiting manual send */}
            <Section
                title="Awaiting your manual send"
                count={approved.length}
                testid="outreach-queue-approved"
                empty="No approved drafts waiting on you. Approve one in /approvals to see it here."
            >
                {approved.map((p) => (
                    <Row
                        key={p.id}
                        prospect={p}
                        campaignName={cmpName(p.campaign_id)}
                        kind="approved"
                    />
                ))}
            </Section>

            {/* Manually sent */}
            <Section
                title="Manually sent (operator-confirmed)"
                count={sent.length}
                testid="outreach-queue-sent"
                empty="Nothing marked manually-sent yet."
            >
                {sent.map((p) => (
                    <Row
                        key={p.id}
                        prospect={p}
                        campaignName={cmpName(p.campaign_id)}
                        kind="manually_sent"
                    />
                ))}
            </Section>
        </div>
    );
}

function Section({ title, count, empty, testid, children }) {
    const isEmpty = !React.Children.count(children);
    return (
        <div className="mb-8" data-testid={testid}>
            <div className="flex items-baseline gap-2 mb-2">
                <h2 className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#9ca3af]">
                    {title}
                </h2>
                <span className="font-mono text-[10px] text-[#6b7280]">{count}</span>
            </div>
            {isEmpty ? (
                <div className="p-6 border border-dashed border-[#262a2e] text-center text-[#9ca3af] font-mono text-[12px]">
                    {empty}
                </div>
            ) : (
                <div className="border border-[#262a2e] rounded-[2px] divide-y divide-[#262a2e]">
                    {children}
                </div>
            )}
        </div>
    );
}

function Row({ prospect: p, campaignName, kind }) {
    const ts =
        kind === "manually_sent"
            ? p.outreach_manually_sent_at
            : p.outreach_approved_at;
    return (
        <div
            className="flex items-center gap-3 px-4 py-3 hover:bg-[#0f1113]"
            data-testid={`outreach-row-${p.id}`}
        >
            <Send
                className={`w-4 h-4 shrink-0 ${
                    kind === "manually_sent" ? "text-[#9ca3af]" : "text-[#7cc93c]"
                }`}
            />
            <div className="flex-1 min-w-0">
                <div className="font-bold text-[13px] truncate">{p.practice_name}</div>
                <div className="font-mono text-[10px] text-[#9ca3af] truncate">
                    {campaignName} ·{" "}
                    {kind === "manually_sent" ? "manually sent" : "approved"}{" "}
                    {ts ? new Date(ts).toLocaleString() : "—"}
                </div>
            </div>
            <ScorePill label="Fit" value={p.fit_score} />
            <ScorePill label="Opp" value={p.opportunity_score} />
            <Link
                to="/approvals"
                className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#9ca3af] hover:text-[#7cc93c] flex items-center gap-1"
                data-testid={`outreach-row-open-${p.id}`}
            >
                Open <ExternalLink className="w-3 h-3" />
            </Link>
        </div>
    );
}
